/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Upload, Settings, BookOpen, LayoutDashboard, Lock, Image as ImageIcon, Sparkles, Share2, Eye, EyeOff, KeyRound, CreditCard, User } from "lucide-react";
import clsx from "clsx";
import { motion } from "framer-motion";
import ButtonLoader from "../ui/loaders/ButtonLoader";
import KidBackground from "../ui/backgrounds/KidBackground";
import MentorAccessCheck from "../components/MentorAccessCheck";
import MentorAccountSettingsModal from "../components/MentorAccountSettingsModal";
import NotificationBell from "../components/NotificationBell";
import { useAuth } from "../context/AuthContext";
import { backendConfig } from "../config/backendConfig";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { authFetch } from "../utils/authFetch";

/** ─────────────────────────────────────────────────────────────────────────────
 * Mentor Dashboard – Artmosphere (Mentor Corner)
 * - Post-profile redirect landing page
 * - Tabs: Base / Intermediate / Advanced
 * - Assignment cards → upload artworks (max 3/assignment/week)
 * - Artwork cards → AI actions (Appreciation / WaysOfSeeing / Reimagine)
 * - Publish modal → Org-only (default) or Mentors Community
 * - School Gallery (passcode-gated view link)
 * - Quick actions: Magic Studio, Storyboarding, Mentor Co-Learning Space
 * NOTE: Replace all TODOs with your Firebase/OpenAI/BFL calls.
 * ──────────────────────────────────────────────────────────────────────────── */

const TABS = ["Base", "Intermediate", "Advanced"];

// Mock data (replace with Firestore)
const MOCK_ASSIGNMENTS = {
  Base: [
    { id: "a1", title: "Color & Mood: Warm vs Cool", summary: "Explore how warm/cool palettes shape emotions.", weeklyLimit: 3 },
    { id: "a2", title: "Lines & Shapes: Visual Rhythm", summary: "Use lines to build movement and balance.", weeklyLimit: 3 },
  ],
  Intermediate: [
    { id: "a3", title: "Composition: Rule of Thirds", summary: "Place your subject to guide the eye.", weeklyLimit: 3 },
  ],
  Advanced: [
    { id: "a4", title: "Interpretation: Story in Still Life", summary: "Narrative through objects, color, and light.", weeklyLimit: 3 },
  ],
};

const Pill = ({ children, className }) => (
  <span className={clsx("inline-flex items-center rounded-full px-3 py-1 text-xs font-medium bg-amber-100 text-amber-700", className)}>{children}</span>
);

export default function MentorDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("Base");
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [artworks, setArtworks] = useState([]); // per-assignment uploads (replace by Firestore query)
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishScope, setPublishScope] = useState("org"); // 'org' | 'mentors'
  const [orgAccessCode, setOrgAccessCode] = useState(""); // fetched from backend
  const [passcodeInput, setPasscodeInput] = useState("");
  const [showPasscodeModal, setShowPasscodeModal] = useState(false);
  const [classNameSelection, setClassNameSelection] = useState(() => {
    try { return localStorage.getItem('mentorClass') || ''; } catch { return ''; }
  });
  const [sectionSelection, setSectionSelection] = useState(() => {
    try { return localStorage.getItem('mentorSection') || ''; } catch { return ''; }
  });
  
  // Account settings and membership state
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showArtHistoryModal, setShowArtHistoryModal] = useState(false);

  console.log('MentorDashboard component rendered');

  // On mount: fetch org access code for School Gallery
  useEffect(() => {
    // TODO: fetch orgAccessCode from Firestore (schools/{schoolId}.orgAccessCode)
    setOrgAccessCode("ART-ORG-2025");
  }, []);

  // Fetch membership status
  useEffect(() => {
    if (user) {
      fetchMembershipStatus();
    }
  }, [user]);

  const fetchMembershipStatus = async () => {
    try {
      setLoading(true);
      console.log('Fetching membership status for user:', user.uid);
      const response = await authFetch(`${backendConfig.baseUrl}/api/membership/status?uid=${user.uid}`);
      if (response.ok) {
        const data = await response.json();
        console.log('Membership status response:', data);
        setMembershipStatus(data.membership);
      } else {
        console.error('Failed to fetch membership status:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching membership status:', error);
    } finally {
      setLoading(false);
    }
  };

  /** Navigate to Co-Learning Space */
  const handleAccessCoLearningSpace = async () => {
    // Check for mentor_session if no Firebase user
    let mentorSession = null;
    try {
      const session = JSON.parse(localStorage.getItem('mentor_session') || '{}');
      if (session && session.ownerId && session.email) {
        mentorSession = session;
      }
    } catch (_) {}

    if (!user && !mentorSession) {
      alert('Please log in to access the co-learning space');
      return;
    }

    try {
      // Get user's parentId for org learning space
      let ownerId = null;
      
      if (user) {
        // Firebase user - get from user document
        try {
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Get parentId - could be stored as parentId, primaryId, or orgId
            ownerId = userData.parentId || userData.primaryId || userData.orgId || user.uid;
            // If user is not primary, get the primary ID
            if (userData.mentorType === 'organization' && !userData.isPrimaryId && userData.primaryId) {
              ownerId = userData.primaryId;
            } else if (userData.isPrimaryId) {
              ownerId = user.uid;
            }
          } else {
            ownerId = user.uid;
          }
        } catch (error) {
          console.error('Error fetching user document:', error);
          ownerId = user.uid;
        }
      } else if (mentorSession) {
        // Mentor session (access code login) - use ownerId from session
        ownerId = mentorSession.ownerId;
      }

      // Navigate to org learning space with ownerId
      if (ownerId) {
        navigate(`/mentors/co-learning?scope=org&ownerId=${encodeURIComponent(ownerId)}`);
      } else {
        navigate('/mentors/co-learning?scope=org');
      }
    } catch (error) {
      console.error('Error accessing co-learning space:', error);
      alert('Failed to access co-learning space. Please try again.');
    }
  };

  const assignments = useMemo(() => MOCK_ASSIGNMENTS[activeTab] || [], [activeTab]);

  /** Upload artwork against selected assignment */
  const handleUploadArtwork = async (files) => {
    if (!selectedAssignment) return;
    // Enforce weekly cap: max 3 uploads/assignment/week (front-end)
    if (artworks.filter(a => a.assignmentId === selectedAssignment.id).length + files.length > selectedAssignment.weeklyLimit) {
      alert(`Upload limit reached. Max ${selectedAssignment.weeklyLimit} uploads per week for this assignment.`);
      return;
    }
    // TODO: upload to Firebase Storage, create Firestore doc
    const newItems = Array.from(files).map((f, idx) => ({
      id: `${selectedAssignment.id}-${Date.now()}-${idx}`,
      assignmentId: selectedAssignment.id,
      fileName: f.name,
      url: URL.createObjectURL(f), // preview only; replace with Storage URL
      ai: { appreciation: null, waysOfSeeing: null, reimagined: [] },
      createdAt: new Date().toISOString(),
    }));
    setArtworks(prev => [...newItems, ...prev]);
  };

  /** AI Actions */
  const runAppreciation = async (artId) => {
    // TODO: call GPT-4o with artwork descriptors (server function)
    updateArtworkAI(artId, { appreciation: { text: "Appreciation generated (demo). Color mood & composition notes here." } });
  };
  const runWaysOfSeeing = async (groupIds) => {
    // TODO: batch call GPT-4o comparative prompt on selected artworks
    groupIds.forEach(id => updateArtworkAI(id, { waysOfSeeing: { text: "Comparative insight generated (demo)." } }));
  };
  const runReimagine = async (artId, variant = "style") => {
    // TODO: call BFL or GPT-image-1 edit for image-to-image transform
    appendReimagined(artId, { url: "#", label: `Variant (${variant})` });
  };

  /** Publish */
  const openPublish = () => setShowPublishModal(true);
  const publishArtwork = async () => {
    // publishScope: 'org' | 'mentors'
    // TODO: write published artifact under mentors_colearning/{orgCode}/… with visibility flag
    alert(`Published to Artmosphere Mentor's Co-learning Space (${publishScope === "org" ? "Org-only" : "Mentors Community"})`);
    setShowPublishModal(false);
  };

  /** Helper mutators */
  const updateArtworkAI = (artId, patch) => {
    setArtworks(prev => prev.map(a => (a.id === artId ? { ...a, ai: { ...a.ai, ...patch } } : a)));
  };
  const appendReimagined = (artId, variantObj) => {
    setArtworks(prev => prev.map(a => (a.id === artId ? { ...a, ai: { ...a.ai, reimagined: [...a.ai.reimagined, variantObj] } } : a)));
  };

  /** Selection helpers for Ways of Seeing (multi-select) */
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const toggleCompareSelect = (id) => {
    setSelectedForCompare(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <MentorAccessCheck>
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-yellow-100 relative">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 0.61, 0.36, 1] }}
          className="relative z-10"
        >
      {/* Refined Navigation Bar */}
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur-md border-b border-gray-200/60 shadow-sm">
        <div className="mx-auto max-w-7xl px-6 py-6">
          {/* Main Header Row */}
          <div className="flex items-center justify-between">
            {/* Left: Artmosphere Branding */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                  <span className="text-white text-xl font-bold">A</span>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">Artmosphere</h1>
                  <p className="text-xs text-gray-500">Creative Community</p>
                </div>
              </div>
              
              {/* Separator */}
              <div className="w-px h-8 bg-gray-300"></div>
              
              {/* Page Title */}
              <div>
                <h2 className="text-2xl font-bold text-gray-900 tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Mentor Dashboard
                </h2>
                <p className="text-sm text-gray-600">Plan, demonstrate, and guide learning</p>
              </div>
            </div>

            {/* Right: User Info and Actions */}
            <div className="flex items-center gap-4">
              
              {/* Status Indicators */}
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-medium">
                  Mentor
                </span>
                {membershipStatus && (
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    membershipStatus.status === 'active' 
                      ? 'bg-emerald-100 text-emerald-800' 
                      : membershipStatus.status === 'trialing'
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {membershipStatus.status === 'active' ? 'Active' : 
                     membershipStatus.status === 'trialing' ? 'Trial' : 'Expired'}
                  </span>
                )}
              </div>

              {/* User Info */}
              <div className="text-right">
                {user?.mentorName && (
                  <p className="text-sm font-medium text-gray-900">{user.mentorName}</p>
                )}
                {user?.email && (
                  <p className="text-xs text-gray-500">{user.email}</p>
                )}
              </div>

              {/* Notification Bell */}
              <NotificationBell />

              {/* Account Button */}
              <button
                onClick={() => setShowAccountSettings(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all duration-200"
              >
                <User className="w-4 h-4" />
                <span className="text-sm font-medium">Account</span>
              </button>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="mt-4 flex items-center justify-center">
            <nav className="flex items-center gap-1 bg-gray-50 rounded-xl p-1">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-all duration-200 text-sm font-medium"
              >
                <LayoutDashboard className="w-4 h-4" />
                Artist Corner
              </button>
              <button
                onClick={() => navigate('/create-books')}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-all duration-200 text-sm font-medium"
              >
                <Sparkles className="w-4 h-4" />
                Collections
              </button>
              <button
                onClick={() => navigate('/mentor-activity-dashboard')}
                className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-all duration-200 text-sm font-medium"
              >
                <LayoutDashboard className="w-4 h-4" />
                Mentor Activity Dashboard
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Mentors Co-Learning Space - Prominent Section */}
      <section className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 border-b border-amber-400/60">
        <div className="mx-auto max-w-7xl px-6 py-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-6 bg-white/95 backdrop-blur-sm rounded-3xl px-8 py-6 shadow-2xl border border-amber-200/60">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-2xl flex items-center justify-center shadow-xl">
                <Share2 className="h-8 w-8 text-white" />
              </div>
              <div className="text-left">
                <h2 className="text-2xl font-bold text-black tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
                  Artmosphere Mentor's Co-learning Space
                </h2>
                <p className="text-black font-medium">
                  See mentors-only submissions (Org or Mentors Community)
                </p>
              </div>
              <button 
                onClick={handleAccessCoLearningSpace}
                className="ml-6 flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl font-semibold"
              >
                <ChevronRight className="w-5 h-5" />
                Access Space
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Quick actions */}
      <motion.section 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 0.61, 0.36, 1] }}
        className="mx-auto max-w-7xl px-6 py-12"
      >
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-black mb-3 tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
            Quick Actions
          </h2>
          <p className="text-black font-medium">Access your essential mentoring tools</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <QuickCard
            title="Art History"
            subtitle="Explore art movements and compare masterpieces"
            icon={<BookOpen className="h-6 w-6" />}
            onClick={() => setShowArtHistoryModal(true)}
          />
          <QuickCard
            title="Assignments & Projects"
            subtitle="Create and manage student assignments"
            icon={<Sparkles className="h-6 w-6" />}
            onClick={() => alert("Go to Assignments & Projects")}
          />
          <QuickCard
            title="Lets Play with Art."
            subtitle="Transform and reimagine student artworks"
            icon={<ImageIcon className="h-6 w-6" />}
            onClick={() => navigate('/mentor-magic')}
          />
        </div>
      </motion.section>


      {/* Account Settings Modal */}
      <MentorAccountSettingsModal 
        isOpen={showAccountSettings} 
        onClose={() => setShowAccountSettings(false)} 
      />

      {/* Art History Modal */}
      {showArtHistoryModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full">
            <div className="bg-gradient-to-r from-amber-500 to-yellow-500 text-white p-8 rounded-t-3xl">
              <div className="flex items-center justify-between">
                <h3 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>Art History Tools</h3>
                <button
                  onClick={() => setShowArtHistoryModal(false)}
                  className="text-white/90 hover:text-white text-3xl leading-none transition-colors duration-200"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-8">
              <p className="text-black mb-8 font-medium">Choose your art history exploration tool:</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <button
                  onClick={() => {
                    setShowArtHistoryModal(false);
                    navigate('/art-appreciation');
                  }}
                  className="flex items-center gap-6 p-8 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl hover:from-amber-100 hover:to-yellow-100 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-2xl flex items-center justify-center text-white shadow-xl">
                    <BookOpen className="w-8 h-8" />
                  </div>
                  <div className="text-left">
                    <h4 className="font-bold text-black text-xl mb-2 tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>Art History Appreciation</h4>
                    <p className="text-black font-medium">Explore art movements and learn about masterpieces</p>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setShowArtHistoryModal(false);
                    navigate('/art-history');
                  }}
                  className="flex items-center gap-6 p-8 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl hover:from-amber-100 hover:to-yellow-100 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-2xl flex items-center justify-center text-white shadow-xl">
                    <ImageIcon className="w-8 h-8" />
                  </div>
                  <div className="text-left">
                    <h4 className="font-bold text-black text-xl mb-2 tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>The Art of Comparison</h4>
                    <p className="text-black font-medium">Compare artworks and teach students about differences</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </motion.div>
      </div>
    </MentorAccessCheck>
  );
}

/** ── Small subcomponents ─────────────────────────────────────────────────── */

function QuickCard({ title, subtitle, icon, onClick }) {
  return (
    <motion.button
      whileHover={{ scale: 1.02, y: -4 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className="group bg-white hover:bg-yellow-50 border border-amber-200 hover:border-yellow-300 rounded-3xl p-8 text-left shadow-lg hover:shadow-2xl transition-all duration-300"
    >
      <div className="flex items-center gap-6">
        <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-2xl flex items-center justify-center text-white shadow-xl group-hover:shadow-2xl transition-all duration-300">
          {icon}
        </div>
        <div className="flex-1">
          <div className="text-black font-bold text-xl mb-2 tracking-tight" style={{ fontFamily: 'Inter, sans-serif' }}>
            {title}
          </div>
          <div className="text-black text-sm leading-relaxed font-medium" style={{ fontFamily: 'Inter, sans-serif' }}>
            {subtitle}
          </div>
        </div>
        <ChevronRight className="h-6 w-6 text-amber-400 group-hover:text-yellow-600 transition-colors duration-300" />
      </div>
    </motion.button>
  );
}

function AssignmentCard({ assignment, active, onOpen }) {
  return (
    <motion.div 
      whileHover={{ scale: 1.02, y: -2 }}
      className={clsx(
        "bg-white/80 hover:bg-white border border-amber-200 hover:border-amber-300 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-200",
        active && "ring-2 ring-amber-500 bg-amber-50"
      )}
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center text-white shadow-md">
          <ImageIcon className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <div className="font-bold text-amber-900 text-lg mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {assignment.title}
          </div>
          <div className="text-amber-700 text-sm leading-relaxed mb-3" style={{ fontFamily: 'Baloo 2, sans-serif' }}>
            {assignment.summary}
          </div>
          <div className="flex items-center gap-2">
            <Pill className="bg-amber-100 text-amber-800 border border-amber-200">
              Max {assignment.weeklyLimit}/week
            </Pill>
          </div>
        </div>
        <button 
          onClick={onOpen} 
          className="px-6 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 transition-all duration-200 shadow-md hover:shadow-lg font-semibold"
        >
          Open
        </button>
      </div>
    </motion.div>
  );
}

function ArtworkCard({ data, selected, onToggleSelect, onAppreciation, onReimagine, onPublish }) {
  return (
    <motion.div 
      whileHover={{ scale: 1.02, y: -2 }}
      className={clsx("card overflow-hidden", selected && "ring-2 ring-amber-400")}
    >
      <div className="aspect-[4/3] bg-amber-50">
        {/* preview */}
        {data.url ? <img src={data.url} alt={data.fileName} className="w-full h-full object-cover" /> : null}
      </div>
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-amber-900 truncate" style={{ fontFamily: 'Fredoka, sans-serif' }}>{data.fileName || "Artwork"}</div>
          <button
            className={clsx("text-xs rounded-full px-2 py-1 border", selected ? "bg-amber-100 text-amber-700 border-amber-300" : "text-amber-600 border-amber-200 hover:bg-amber-50")}
            onClick={onToggleSelect}
            title="Select for Ways of Seeing (comparison)"
          >
            {selected ? "Selected" : "Select"}
          </button>
        </div>

        {/* AI actions */}
        <div className="grid grid-cols-3 gap-2">
          <button onClick={onAppreciation} className="inline-flex items-center justify-center gap-1 rounded-xl border px-2 py-2 text-xs hover:bg-amber-50 border-amber-200 text-amber-700">
            <Sparkles className="h-3.5 w-3.5" /> Appreciation
          </button>
          <button onClick={() => onReimagine("style")} className="inline-flex items-center justify-center gap-1 rounded-xl border px-2 py-2 text-xs hover:bg-amber-50 border-amber-200 text-amber-700">
            <Eye className="h-3.5 w-3.5" /> Reimagine
          </button>
          <button onClick={onPublish} className="inline-flex items-center justify-center gap-1 rounded-xl border px-2 py-2 text-xs hover:bg-amber-50 border-amber-200 text-amber-700">
            <Share2 className="h-3.5 w-3.5" /> Publish
          </button>
        </div>

        {/* AI results (demo placeholders) */}
        <div className="space-y-2">
          {data.ai?.appreciation && (
            <div className="rounded-lg bg-amber-50 p-2 text-xs text-amber-700">
              <b style={{ fontFamily: 'Fredoka, sans-serif' }}>Appreciation:</b> <span style={{ fontFamily: 'Baloo 2, sans-serif' }}>{data.ai.appreciation.text}</span>
            </div>
          )}
          {data.ai?.waysOfSeeing && (
            <div className="rounded-lg bg-amber-50 p-2 text-xs text-amber-700">
              <b style={{ fontFamily: 'Fredoka, sans-serif' }}>Ways of Seeing:</b> <span style={{ fontFamily: 'Baloo 2, sans-serif' }}>{data.ai.waysOfSeeing.text}</span>
            </div>
          )}
          {Array.isArray(data.ai?.reimagined) && data.ai.reimagined.length > 0 && (
            <div className="rounded-lg bg-amber-50 p-2 text-xs text-amber-700">
              <b style={{ fontFamily: 'Fredoka, sans-serif' }}>Reimagined:</b> <span style={{ fontFamily: 'Baloo 2, sans-serif' }}>{data.ai.reimagined.length} variant(s)</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
